import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Loader2, Search, UserMinus, Users, Mail, BookOpen, Shield, Download } from "lucide-react";

const SubscribersPage = () => {
  const { token } = useAuth();
  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", category: "website", source: "" });
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchAll(); }, [catFilter]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      let url = `${API}/staff/all-subscribers?limit=200`;
      if (catFilter !== "all") url += `&category=${catFilter}`;
      const [sRes, mRes] = await Promise.all([
        fetch(url, { headers }),
        fetch(`${API}/staff/marketing-stats`, { headers }),
      ]);
      if (sRes.ok) { const d = await sRes.json(); setSubscribers(d.subscribers); setTotal(d.total); }
      if (mRes.ok) setStats(await mRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!form.email) { toast.error("Email is required"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/staff/subscribers`, {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) { toast.success("Subscriber added"); setShowAdd(false); setForm({ email: "", name: "", category: "website", source: "" }); fetchAll(); }
      else { const e = await res.json(); toast.error(e.detail || "Failed"); }
    } catch { toast.error("Connection error"); }
    finally { setSaving(false); }
  };

  const handleUnsubscribe = async (id) => {
    try {
      const res = await fetch(`${API}/staff/subscribers/${id}/unsubscribe`, { method: "PUT", headers });
      if (res.ok) { toast.success("Unsubscribed"); fetchAll(); }
    } catch { toast.error("Failed"); }
  };

  const filtered = search
    ? subscribers.filter(s => s.email?.toLowerCase().includes(search.toLowerCase()) || s.name?.toLowerCase().includes(search.toLowerCase()))
    : subscribers;

  const catBadge = (c) => {
    const map = {
      website: { cls: "bg-blue-50 text-blue-700 border-blue-200", icon: Mail },
      resource_download: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Download },
      patient_opted_in: { cls: "bg-green-50 text-green-700 border-green-200", icon: Shield },
    };
    const cfg = map[c] || map.website;
    const Icon = cfg.icon;
    return <Badge className={`${cfg.cls} text-xs`}><Icon className="w-3 h-3 mr-1" />{c?.replace(/_/g, " ")}</Badge>;
  };

  if (loading) return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

  return (
    <div className="space-y-6" data-testid="subscribers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">Subscribers</h1>
          <p className="mt-1 text-base text-stone-500">Manage newsletter subscribers across 3 categories.</p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg" data-testid="add-subscriber-btn">
          <UserPlus className="w-4 h-4 mr-1" /> Add Subscriber
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <MiniStat label="Total Active" value={stats.subscribers?.total_active || 0} icon={Users} color="text-[#FF7A00]" />
          <MiniStat label="Website" value={stats.subscribers?.website || 0} icon={Mail} color="text-blue-600" />
          <MiniStat label="Resource DL" value={stats.subscribers?.resource_downloaders || 0} icon={Download} color="text-amber-600" />
          <MiniStat label="Patients Opted In" value={stats.subscribers?.patients_opted_in || 0} icon={Shield} color="text-green-600" />
          <MiniStat label="Unsubscribed" value={stats.subscribers?.unsubscribed || 0} icon={UserMinus} color="text-stone-400" />
        </div>
      )}

      {/* Marketing Consent Note */}
      {stats?.consent && (
        <Card className="rounded-xl border-stone-200/60 p-4 bg-stone-50">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-green-600 shrink-0" />
            <div>
              <p className="text-sm font-medium text-stone-900">Marketing consent (patients)</p>
              <p className="text-xs text-stone-500">
                {stats.consent.marketing_granted} patients have granted marketing consent. {stats.consent.marketing_withdrawn} have withdrawn.
                Marketing emails are only sent to patients who have active consent. This is tracked separately from clinical data consent.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by email or name..." className="pl-10 rounded-lg" data-testid="sub-search" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-48 rounded-lg" data-testid="sub-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            <SelectItem value="website">Website subscribers</SelectItem>
            <SelectItem value="resource_download">Resource downloaders</SelectItem>
            <SelectItem value="patient_opted_in">Patients opted in</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Add Form */}
      {showAdd && (
        <Card className="rounded-xl border-[#FF7A00]/30 p-5 space-y-3" data-testid="add-form">
          <h3 className="font-bold text-stone-900 text-sm">Add subscriber manually</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email address" className="rounded-lg" data-testid="add-email" />
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Name (optional)" className="rounded-lg" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
              <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="website">Website subscriber</SelectItem>
                <SelectItem value="resource_download">Resource downloader</SelectItem>
                <SelectItem value="patient_opted_in">Patient opted in</SelectItem>
              </SelectContent>
            </Select>
            <Input value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} placeholder="Source (optional)" className="rounded-lg" />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAdd} disabled={saving} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg text-sm" data-testid="add-confirm">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />} Add
            </Button>
            <Button variant="ghost" onClick={() => setShowAdd(false)} className="text-sm">Cancel</Button>
          </div>
        </Card>
      )}

      {/* Subscriber List */}
      {filtered.length > 0 ? (
        <Card className="rounded-xl border-stone-200/60 overflow-hidden">
          <div className="hidden sm:grid grid-cols-[2fr_1fr_1fr_1fr_80px] gap-4 px-5 py-3 bg-stone-50 text-xs font-semibold text-stone-500 uppercase tracking-wider border-b border-stone-100">
            <span>Email</span><span>Name</span><span>Category</span><span>Date</span><span></span>
          </div>
          <div className="divide-y divide-stone-100">
            {filtered.map(s => (
              <div key={s.subscriber_id} className="px-5 py-3 sm:grid sm:grid-cols-[2fr_1fr_1fr_1fr_80px] sm:gap-4 sm:items-center" data-testid={`sub-${s.subscriber_id}`}>
                <div>
                  <p className="text-sm text-stone-900">{s.email}</p>
                  {!s.is_active && <Badge className="bg-red-50 text-red-600 text-xs mt-0.5">Unsubscribed</Badge>}
                </div>
                <p className="text-sm text-stone-600 hidden sm:block">{s.name || "-"}</p>
                <div className="hidden sm:block">{catBadge(s.category)}</div>
                <p className="text-xs text-stone-400 hidden sm:block">{s.subscribed_at?.split("T")[0]}</p>
                <div className="hidden sm:block">
                  {s.is_active && (
                    <Button size="sm" variant="ghost" className="text-xs text-stone-400 hover:text-red-500" onClick={() => handleUnsubscribe(s.subscriber_id)}>
                      <UserMinus className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <Card className="rounded-xl border-stone-200/60 p-12 text-center">
          <Users className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <p className="text-sm text-stone-500">No subscribers found.</p>
        </Card>
      )}
    </div>
  );
};

const MiniStat = ({ label, value, icon: Icon, color = "text-stone-700" }) => (
  <Card className="rounded-xl border-stone-200/60 p-3 text-center">
    <Icon className={`w-4 h-4 mx-auto mb-1 ${color}`} />
    <p className={`text-lg font-bold ${color}`}>{value}</p>
    <p className="text-xs text-stone-400">{label}</p>
  </Card>
);

export default SubscribersPage;
